import { memo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useImagePreloader from "../hooks/useImagePreloader";
import LoadingOverlay from "../components/LoadingOverlay";

const TermsSection = memo(({ title, content }) => (
  <section>
    <h2 className="text-xl font-semibold text-[#009fd6] mb-4">{title}</h2>
    {content.map((paragraph, index) => (
      <p key={index} className="mb-4">
        {paragraph}
      </p>
    ))}
  </section>
));

TermsSection.displayName = "TermsSection";

TermsSection.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function TermsAndConditions() {
  useDocumentTitle("termsAndConditions");
  const { t } = useTranslation();

  const termsData = t("components.termsAndConditions", { returnObjects: true });
  const sectionNames = Object.keys(termsData.sections);

  const loading = useImagePreloader([]);

  return (
    <>
      {loading && <LoadingOverlay />}
      <div className="bg-black text-gray-200 pt-24 pb-16 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-white mb-8" tabIndex="0">
              {termsData.title}
            </h1>

            <div className="space-y-8">
              {sectionNames.map((sectionKey) => (
                <TermsSection
                  key={sectionKey}
                  title={termsData.sections[sectionKey].title}
                  content={termsData.sections[sectionKey].content}
                />
              ))}
            </div>

            <div className="border-t border-gray-700 pt-6 mt-8">
              <p className="text-sm text-gray-400">{termsData.lastUpdated}</p>
            </div>

            <div className="mt-12 flex justify-center">
              <Link to="/">
                <button
                  className="bg-[#009fd6] text-white px-6 py-3 rounded-lg hover:bg-[#007cab] transition-colors"
                  aria-label={termsData.returnToHome}
                >
                  {termsData.returnToHome}
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default memo(TermsAndConditions);
